/**
 * Export Invoice Modal
 * Allows TG to select and export assignments ready for invoicing
 */

import { useState, useMemo } from 'react'
import { X, Download, FileSpreadsheet, MapPin, Check, Loader2, Info, AlertTriangle } from 'lucide-react'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import { supabase } from '../lib/supabase'
import { generateInvoiceExcel, downloadExcel, type InvoiceExportAssignment } from '../services/invoiceExport'
import toast from 'react-hot-toast'

interface Assignment {
  id: string
  assignment_number: number | null
  title: string
  start_date: string
  customer_name: string
  location: string | null
  tg_personal_invoice_marked: boolean
}

interface ExportInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  assignments: Assignment[]
  subcontractorId: string
  onExportComplete?: () => void
}

export function ExportInvoiceModal({
  isOpen,
  onClose,
  assignments,
  subcontractorId,
  onExportComplete
}: ExportInvoiceModalProps) {
  // Only show assignments not already marked as invoiced
  const exportableAssignments = useMemo(() =>
    assignments.filter(a => !a.tg_personal_invoice_marked),
    [assignments]
  )

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() =>
    new Set(exportableAssignments.map(a => a.id))
  )
  const [markAsInvoiced, setMarkAsInvoiced] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  // Group assignments by date
  const groupedAssignments = useMemo(() => {
    const groups: Record<string, Assignment[]> = {}
    for (const assignment of exportableAssignments) {
      if (!groups[assignment.start_date]) {
        groups[assignment.start_date] = []
      }
      groups[assignment.start_date].push(assignment)
    }
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a)) // Sort descending (newest first)
  }, [exportableAssignments])

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(new Set(exportableAssignments.map(a => a.id)))
  }

  const selectNone = () => {
    setSelectedIds(new Set())
  }

  const handleExportClick = () => {
    if (selectedIds.size === 0) return

    // If marking as invoiced, show confirmation first
    if (markAsInvoiced) {
      setShowConfirmation(true)
    } else {
      handleExport()
    }
  }

  const handleExport = async () => {
    if (selectedIds.size === 0) return
    setShowConfirmation(false)

    setIsExporting(true)

    try {
      const selectedAssignmentIds = Array.from(selectedIds)

      // Fetch TG's price list
      const { data: subcontractorData } = await supabase
        .from('subcontractors')
        .select('price_list_id')
        .eq('id', subcontractorId)
        .single()

      // Fetch prices from TG's price list
      let priceMap: Record<string, number> = {}
      if (subcontractorData?.price_list_id) {
        const { data: priceItems } = await supabase
          .from('price_list_articles')
          .select('article_id, price_excl_vat')
          .eq('price_list_id', subcontractorData.price_list_id)

        if (priceItems) {
          priceMap = Object.fromEntries(
            priceItems.map(item => [item.article_id, item.price_excl_vat])
          )
        }
      }

      // Fetch full report data for selected assignments
      const { data: reportData, error } = await supabase
        .from('assignment_subcontractors')
        .select(`
          assignment_id,
          report_text,
          reported_articles,
          assignments (
            id,
            assignment_number,
            title,
            start_date,
            location
          )
        `)
        .eq('subcontractor_id', subcontractorId)
        .in('assignment_id', selectedAssignmentIds)

      if (error) throw error

      // Transform to export format
      const exportData: InvoiceExportAssignment[] = (reportData || []).map(row => {
        const assignment = row.assignments as any
        const articles = (row.reported_articles as any[]) || []

        // Add prices from price list to each article (preserve expense fields)
        const articlesWithPrices = articles.map(article => ({
          article_id: article.article_id,
          article_name: article.article_name,
          actual_quantity: article.actual_quantity,
          unit: article.unit || null,
          unit_price: priceMap[article.article_id] || null,
          notes: article.notes,
          // Utlägg-fält
          is_expense: article.is_expense || false,
          expense_amount_incl_vat: article.expense_amount_incl_vat || null,
          expense_amount_excl_vat: article.expense_amount_excl_vat || null,
          expense_vat_rate: article.expense_vat_rate ?? null
        }))

        return {
          id: assignment?.id,
          assignment_number: assignment?.assignment_number,
          title: assignment?.title || '',
          start_date: assignment?.start_date || '',
          location: typeof assignment?.location === 'string'
            ? assignment.location
            : (assignment?.location as any)?.address || null,
          report_text: row.report_text,
          reported_articles: articlesWithPrices
        }
      })

      // Sort by date
      exportData.sort((a, b) => a.start_date.localeCompare(b.start_date))

      // Generate and download Excel
      const workbook = generateInvoiceExcel(exportData)
      const dateStr = format(new Date(), 'yyyy-MM-dd')
      const filename = `faktureringsunderlag-${dateStr}.xlsx`
      downloadExcel(workbook, filename)

      // Mark as invoiced if checkbox is checked
      if (markAsInvoiced) {
        const { error: updateError } = await supabase
          .from('assignment_subcontractors')
          .update({ tg_personal_invoice_marked: true })
          .eq('subcontractor_id', subcontractorId)
          .in('assignment_id', selectedAssignmentIds)

        if (updateError) {
          console.error('Error marking as invoiced:', updateError)
          toast.error('Exporten lyckades men kunde inte markera som fakturerade')
        } else {
          toast.success(`${selectedIds.size} uppdrag exporterade och markerade som fakturerade`)
        }
      } else {
        toast.success(`${selectedIds.size} uppdrag exporterade`)
      }

      onExportComplete?.()
      onClose()
    } catch (err) {
      console.error('Export error:', err)
      toast.error('Kunde inte exportera data')
    } finally {
      setIsExporting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white dark:bg-nature-800 rounded-xl shadow-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-nature-200 dark:border-nature-700">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
              Exportera faktureringsunderlag
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors"
          >
            <X className="h-5 w-5 text-nature-500" />
          </button>
        </div>

        {/* Info box */}
        <div className="px-4 py-3 border-b border-nature-200 dark:border-nature-700 bg-blue-50 dark:bg-blue-900/20">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Så här fungerar det:</p>
              <ol className="list-decimal list-inside space-y-0.5 text-blue-700 dark:text-blue-300">
                <li>Välj vilka uppdrag du vill exportera</li>
                <li>Filen innehåller rapporterade artiklar och anteckningar</li>
                <li>Öppna filen i Excel</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="px-4 py-2 border-b border-nature-200 dark:border-nature-700 bg-amber-50 dark:bg-amber-900/20">
          <p className="text-xs text-amber-700 dark:text-amber-300">
            <strong>OBS:</strong> Detta är ett automatiskt genererat underlag. Kontrollera att uppgifterna stämmer innan du fakturerar.
          </p>
        </div>

        {/* Selection controls */}
        <div className="px-4 py-2 border-b border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900/50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-nature-600 dark:text-nature-400">
              {selectedIds.size} av {exportableAssignments.length} valda
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-2 py-1 text-xs rounded bg-nature-200 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Välj alla
              </button>
              <button
                onClick={selectNone}
                className="px-2 py-1 text-xs rounded bg-nature-200 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Avmarkera alla
              </button>
            </div>
          </div>
        </div>

        {/* Assignment list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {exportableAssignments.length === 0 ? (
            <p className="text-center text-nature-500 dark:text-nature-400 py-8">
              Inga uppdrag att exportera. Alla godkända uppdrag är redan markerade som fakturerade.
            </p>
          ) : (
            groupedAssignments.map(([date, dateAssignments]) => (
              <div key={date}>
                {/* Date header */}
                <h3 className="text-sm font-medium text-nature-700 dark:text-nature-300 mb-2 capitalize">
                  {format(new Date(date), 'EEEE', { locale: sv })} {date}
                </h3>

                {/* Assignments for this date */}
                <div className="space-y-2">
                  {dateAssignments.map(assignment => {
                    const isSelected = selectedIds.has(assignment.id)
                    return (
                      <button
                        key={assignment.id}
                        onClick={() => toggleSelect(assignment.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          isSelected
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                            : 'border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <div className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-colors ${
                            isSelected
                              ? 'bg-green-600 border-green-600'
                              : 'border-nature-300 dark:border-nature-600'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>

                          {/* Assignment info */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-nature-900 dark:text-nature-100 truncate">
                              {assignment.assignment_number && (
                                <span className="text-nature-500 dark:text-nature-400 mr-1">
                                  #{assignment.assignment_number}
                                </span>
                              )}
                              {assignment.title}
                            </h4>

                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-nature-500 dark:text-nature-400">
                              {assignment.customer_name && (
                                <span>{assignment.customer_name}</span>
                              )}
                              {assignment.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  <span className="truncate max-w-[150px]">{assignment.location}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Mark as invoiced checkbox */}
        {exportableAssignments.length > 0 && (
          <div className="px-4 py-3 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900/50">
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                onClick={() => setMarkAsInvoiced(!markAsInvoiced)}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  markAsInvoiced
                    ? 'bg-green-600 border-green-600'
                    : 'border-nature-300 dark:border-nature-600'
                }`}
              >
                {markAsInvoiced && <Check className="h-3 w-3 text-white" />}
              </div>
              <span
                onClick={() => setMarkAsInvoiced(!markAsInvoiced)}
                className="text-sm text-nature-700 dark:text-nature-300"
              >
                Vill du markera de exporterade uppdragen som faktura skickad?
              </span>
            </label>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-3 border-t border-nature-200 dark:border-nature-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-nature-300 dark:border-nature-600 text-nature-700 dark:text-nature-300 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
          >
            Avbryt
          </button>
          <button
            onClick={handleExportClick}
            disabled={selectedIds.size === 0 || isExporting}
            className="px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isExporting ? 'Exporterar...' : `Exportera ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
          </button>
        </div>
      </div>

      {/* Confirmation dialog */}
      {showConfirmation && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowConfirmation(false)} />
          <div className="relative bg-white dark:bg-nature-800 rounded-xl shadow-xl p-6 max-w-sm mx-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-2">
                  Bekräfta markering
                </h3>
                <p className="text-sm text-nature-600 dark:text-nature-400 mb-4">
                  Du har valt att markera {selectedIds.size} uppdrag som fakturerade.
                  De kommer inte längre visas i listan för export.
                </p>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setShowConfirmation(false)}
                    className="px-4 py-2 text-sm rounded-lg border border-nature-300 dark:border-nature-600 text-nature-700 dark:text-nature-300 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={handleExport}
                    className="px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                  >
                    Ja, exportera och markera
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
